import { FC } from 'react';

import { getVisibleTickMarks, getYLocationForDepthInM, LengthIndicator, TickEnum } from '@da-g3-acl/shared/common';

import { useCalculateAnchorPositions } from '../../../../hooks/use-calculate-anchor-positons';
import { LengthRange } from '../../../../types/lengthRange';

import './style.scss';

export interface HorizontalTicksProps {
	widthInPx: number;
	range: LengthRange;
	lengthIndicators: LengthIndicator[] | undefined;
	onIndicatorHover?: ((indicator: LengthIndicator | undefined) => void) | undefined;
}

const tickBaseLength = 3;
const tickMetreLength = tickBaseLength + 5;

const HorizontalTicks: FC<HorizontalTicksProps> = (props: HorizontalTicksProps) => {
	const { range, lengthIndicators, onIndicatorHover, widthInPx } = props;

	const rangeInM = range.depthTo - range.depthFrom;
	const pxPerM = widthInPx / rangeInM;

	const visibleTickMarks = getVisibleTickMarks({
		pxPerM,
		rulerRangeM: range,
		labelFrequencyTicks: 10,
	});

	const indicatorPositions = useCalculateAnchorPositions({
		lengthIndicators,
		visibleRange: range,
	});

	return (
		<svg className="horizontal-ruler">
			<>
				<g>
					{visibleTickMarks.map(
						(r) =>
							r.tickDepthMetres >= 0 && (
								<g key={`tick-${r.tickDepthMetres}`}>
									<line
										x1={getYLocationForDepthInM(pxPerM, r.tickDepthMetres, range.depthFrom)}
										x2={getYLocationForDepthInM(pxPerM, r.tickDepthMetres, range.depthFrom)}
										y1={0}
										y2={
											r.type === TickEnum.Long || r.type === TickEnum.Label
												? tickMetreLength
												: tickBaseLength
										}
										stroke={'#283C50'}
										strokeWidth={1}
										shapeRendering={'crispEdges'}
									/>
									{r.type === TickEnum.Label && (
										<text
											className={'horizontal-ruler-label'}
											x={getYLocationForDepthInM(pxPerM, r.tickDepthMetres, range.depthFrom)}
											y={'100%'}
											style={{
												transform: `translateY(-4px) translateX(${calculateLabelOffset(
													r.tickDepthMetres,
												)}px)`,
											}}
											textAnchor="end"
											shapeRendering={'crispEdges'}
											textRendering={'optimizeLegibility'}
										>
											{r.tickDepthMetres}
										</text>
									)}
								</g>
							),
					)}
				</g>
				<g>
					{indicatorPositions.map((r) => {
						const yLocation = getYLocationForDepthInM(pxPerM, r.lengthM, range.depthFrom);
						return (
							<g key={`indicator-${yLocation}`}>
								<line
									x1={yLocation}
									x2={yLocation + 18}
									y1={0}
									y2={6}
									stroke={'grey'}
									strokeWidth={2}
								/>
								{r.lengthIndicators.map((l, i) => (
									<text
										onMouseEnter={() => onIndicatorHover && onIndicatorHover(l)}
										onMouseLeave={() => onIndicatorHover && onIndicatorHover(undefined)}
										key={`${yLocation}-${i}`}
										className={'lengthIndicator'}
										x={yLocation + 24 + i * 12}
										y={10}
										textAnchor="middle"
										fill={l.fillColour}
										stroke={l.fillColour}
										fontSize={20}
									>
										{l.indicator}
									</text>
								))}
							</g>
						);
					})}
				</g>
			</>
		</svg>
	);
};

export default HorizontalTicks;

const calculateLabelOffset = (labelValue: number): number => {
	const labelString = labelValue.toString();
	return labelString.length > 3 ? 10 : 5;
};
